import React from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Save, Loader2 } from "lucide-react";

interface FormActionBarProps {
  hasChanges: boolean;
  hasValidChanges: boolean;
  isSubmitting: boolean;
  mode: "create" | "edit";
  onSubmit: () => void;
  onCancel: () => void;
  className?: string;
}

export default function FormActionBar({
  hasChanges,
  hasValidChanges,
  isSubmitting,
  mode,
  onSubmit,
  onCancel,
  className
}: FormActionBarProps) {
  return (
    <div className={cn(
              "border-t border-border bg-background prescription-action-bar",
      className
    )}>
      <div className="px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Left side - Simple status indicator */}
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {isSubmitting && (
              <div className="flex items-center gap-2 text-sm text-primary bg-primary/10 px-3 py-1.5 rounded-md border border-primary/20">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="font-medium">Saving...</span>
              </div>
            )}
            
            {!isSubmitting && hasChanges && (
              <div className="flex items-center gap-2 text-sm text-warning bg-warning/10 px-3 py-1.5 rounded-md border border-warning/20">
                <div className="w-2 h-2 bg-warning rounded-full animate-pulse"></div>
                <span className="font-medium">Unsaved changes</span>
              </div>
            )}
            
            {!isSubmitting && !hasChanges && (
              <div className="flex items-center gap-2 text-sm text-success bg-success/10 px-3 py-1.5 rounded-md border border-success/20">
                <div className="w-2 h-2 bg-success rounded-full"></div>
                <span className="font-medium">All changes saved</span>
              </div>
            )}
          </div>

          {/* Right side - Action buttons */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
              className="h-9 px-4 text-sm font-medium"
            >
              Cancel
            </Button>
            
            <Button
              type="submit"
              onClick={onSubmit}
              disabled={!hasValidChanges || isSubmitting}
              className={cn(
                "h-9 px-4 text-sm font-medium",
                "bg-primary hover:bg-primary/90 text-primary-foreground",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  <span>
                    {mode === "edit" ? "Update" : "Save"} Prescription
                  </span>
                </div>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
} 